import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, Settings, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/store/auth.store';

interface VaultUserLayoutProps {
  /** Page title displayed in the header */
  title: string;
  /** Optional subtitle displayed below the title */
  subtitle?: string;
  /** Show settings button in header (default: false) */
  showSettings?: boolean;
  /** Show back button instead of logo (default: false) */
  showBackButton?: boolean;
  /** Custom back navigation path (default: /my-vaults) */
  backPath?: string;
  /** Page content */
  children: ReactNode;
  /** Optional right side actions to display before logout button */
  headerActions?: ReactNode;
}

/**
 * VaultUserLayout
 *
 * Shared layout for vault user pages (employees/vault_user role).
 * Provides consistent header with logo, title, and navigation.
 */
export const VaultUserLayout = ({
  title,
  subtitle,
  showSettings = false,
  showBackButton = false,
  backPath,
  children,
  headerActions,
}: VaultUserLayoutProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            {showBackButton ? (
              <button
                onClick={handleBack}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
                <Shield className="h-5 w-5 text-primary-600" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-semibold text-neutral-900">{title}</h1>
              {subtitle && (
                <p className="text-sm text-neutral-500">{subtitle}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {headerActions}
            {showSettings && (
              <button
                onClick={() => navigate('/settings')}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
                title={t('common.settings', 'Paramètres')}
              >
                <Settings className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t('common.logout', 'Déconnexion')}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
    </div>
  );
};
